import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  FileText,
  Bell,
  Search,
  User,
  LogOut,
  Menu,
  X,
  Building,
  Clock,
  Target,
  CheckSquare,
  Calendar,
  CalendarDays,
  Trash2,
  Heart,
  Package,
  CreditCard,
  UserCircle,
  Briefcase,
  CalendarCheck,
  Settings,
  DoorOpen,
  Receipt,
  Lock,
  AlertTriangle,
  ChevronDown,
  Tag
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import styles from './BusinessLayout.module.css';
import { apiServiceWithRefresh } from '../../utils/api';
import SupportWidget from '../common/SupportWidget/SupportWidget';

const BusinessLayout = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationSearch, setNotificationSearch] = useState('');
  const [notificationTypeFilter, setNotificationTypeFilter] = useState('all');
  const [notificationReadFilter, setNotificationReadFilter] = useState('all');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPages, setRecentPages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { logout, user, hasFullAccess, isInGracePeriod } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const locallyReadIdsRef = useRef(new Set());

  const isOwner = user?.role === 1;
  const isManager = user?.role === 2;

  const displayName = isOwner
    ? (user?.businessName || user?.email?.split('@')[0] || 'User')
    : (user?.preferredName || user?.firstName || user?.email?.split('@')[0] || 'User');

  const roleLabel = isOwner ? 'Business Owner' : (isManager ? 'Manager' : 'Employee');

  // Redirect to billing if subscription not active
  useEffect(() => {
    if (!hasFullAccess && location.pathname !== '/business/billing') {
      navigate('/business/billing', { replace: true });
    }
  }, [hasFullAccess, location.pathname, navigate]);

  const isDarkMode = location.pathname.includes('dashboard-dark');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (item) => {
    if (item.path) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  };

  const handleDismissNotification = async (notificationId) => {
    try {
      const result = await apiServiceWithRefresh.dismissNotification(notificationId);
      if (result.success) {
        removeDismissedId(notificationId);
        setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
      }
    } catch (error) {
      // Silently handle dismiss failure
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getDismissedIds = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem('dismissedNotifications') || '[]'));
    } catch { return new Set(); }
  };

  const saveDismissedId = (id) => {
    const ids = getDismissedIds();
    ids.add(id);
    const idsArray = [...ids];
    const capped = idsArray.length > 500 ? idsArray.slice(idsArray.length - 500) : idsArray;
    localStorage.setItem('dismissedNotifications', JSON.stringify(capped));
  };

  const removeDismissedId = (id) => {
    const ids = getDismissedIds();
    ids.delete(id);
    localStorage.setItem('dismissedNotifications', JSON.stringify([...ids]));
  };

  const handleDismissFromDropdown = (notificationId) => {
    saveDismissedId(notificationId);
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, dismissed: true } : n)
    );
  };

  const filteredModalNotifications = notifications.filter(n => {
    if (notificationSearch) {
      const query = notificationSearch.toLowerCase();
      if (!n.title.toLowerCase().includes(query) && !n.message.toLowerCase().includes(query)) return false;
    }
    if (notificationTypeFilter !== 'all' && n.type !== notificationTypeFilter) return false;
    if (notificationReadFilter === 'unread' && n.read) return false;
    if (notificationReadFilter === 'read' && !n.read) return false;
    return true;
  });

  const handleShowAllMessages = () => {
    setIsNotificationDropdownOpen(false);
    setNotificationSearch('');
    setNotificationTypeFilter('all');
    setNotificationReadFilter('all');
    setIsNotificationModalOpen(true);
  };

  const handleViewBusinessProfile = () => {
    navigate('/business-profile');
    setIsProfileDropdownOpen(false);
  };

  const handleViewBusinessSettings = () => {
    navigate('/business-settings');
    setIsProfileDropdownOpen(false);
  };

  const handleViewBilling = () => {
    navigate('/business/billing');
    setIsProfileDropdownOpen(false);
  };

  const handleViewPersonalProfile = () => {
    navigate('/employee/profile');
    setIsProfileDropdownOpen(false);
  };

  // Fetch notifications via useQuery with polling
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'business'],
    queryFn: async () => {
      const result = await apiServiceWithRefresh.getNotifications();
      if (result.success) {
        return result.data || [];
      }
      return [];
    },
    refetchInterval: 300000,
  });

  // Process notifications data
  useEffect(() => {
    if (notificationsData) {
      const dismissedIds = getDismissedIds();
      const formattedNotifications = notificationsData.map(notif => {
        const isLocallyRead = locallyReadIdsRef.current.has(notif.id);
        if (notif.isRead && isLocallyRead) {
          locallyReadIdsRef.current.delete(notif.id);
        }
        return {
          id: notif.id,
          title: notif.title,
          message: notif.message,
          time: formatRelativeTime(notif.createdAt),
          type: notif.type,
          read: notif.isRead || isLocallyRead,
          dismissed: dismissedIds.has(notif.id)
        };
      });
      setNotifications(formattedNotifications);
    }
  }, [notificationsData]);

  // Auto-mark notifications as read when modal opens
  useEffect(() => {
    if (isNotificationModalOpen) {
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        unreadNotifications.forEach(n => locallyReadIdsRef.current.add(n.id));
        setNotifications(prev =>
          prev.map(n => n.read ? n : { ...n, read: true })
        );
        unreadNotifications.forEach(n => {
          apiServiceWithRefresh.markNotificationAsRead(n.id).catch(() => {});
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotificationModalOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setIsNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load recent pages from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentPagesBusinessLayout');
    if (saved) {
      try {
        setRecentPages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent pages');
      }
    }
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Menu items
  const menuItems = useMemo(() => isOwner || isManager ? [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    {
      id: 'sales-inventory',
      label: 'Sales & Inventory',
      icon: CreditCard,
      hasDropdown: true,
      children: [
        { id: 'pos', label: 'Point of Sale', icon: CreditCard, path: '/business/pos' },
        { id: 'stock', label: 'Stock Management', icon: Package, path: '/business/stock' },
        { id: 'vouchers', label: 'Vouchers', icon: Tag, path: '/business/vouchers' }
      ]
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: UserCircle,
      hasDropdown: true,
      children: [
        { id: 'clients-list', label: 'Clients', icon: UserCircle, path: '/business/clients' },
        { id: 'bookings', label: 'Bookings', icon: CalendarDays, path: '/business/bookings' },
        { id: 'manage-services', label: 'Manage Services', icon: Briefcase, path: '/business/services' },
        { id: 'workstations', label: 'Workstations', icon: DoorOpen, path: '/business/workstations' }
      ]
    },
    {
      id: 'employees',
      label: 'Employees',
      icon: Users,
      hasDropdown: true,
      children: [
        { id: 'employees-list', label: 'Employees', icon: Users, path: '/employees' },
        // [DISABLED] Leave — hidden per business decision, re-enable when ready to launch as new features
        // { id: 'my-leave', label: 'My Leave', icon: Calendar, path: '/business/my-leave' },
        // { id: 'leave-management', label: 'Leave Management', icon: CalendarCheck, path: '/business/leave' }
      ]
    },
    { id: 'time-tracking', label: 'Time Tracking', icon: Clock, path: '/business/time-tracking' },
    {
      id: 'checklists-goals',
      label: 'Checklists & Goals',
      icon: CheckSquare,
      hasDropdown: true,
      children: [
        // [DISABLED] Checklists — hidden per business decision, re-enable when ready to launch as new features
        // { id: 'checklists', label: 'Checklists', icon: CheckSquare, path: '/checklists' },
        { id: 'goal-setting', label: 'Goal Setting', icon: Target, path: '/business/goal-setting' }
      ]
    },
    {
      id: 'business-profile',
      label: 'Business Profile',
      icon: Building,
      hasDropdown: true,
      children: [
        { id: 'business-profile-main', label: 'Business Profile', icon: Building, path: '/business-profile' },
        // [DISABLED] HR Policies & MVV — hidden per business decision, re-enable when ready to launch as new features
        // { id: 'policies', label: 'HR Policies', icon: FileText, path: '/policies' },
        // { id: 'company-values', label: 'Mission & Values', icon: Heart, path: '/company-values' },
        { id: 'billing', label: 'Billing', icon: Receipt, path: '/business/billing' }
      ]
    }
  ] : [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    {
      id: 'sales-inventory',
      label: 'Sales & Inventory',
      icon: CreditCard,
      hasDropdown: true,
      children: [
        { id: 'pos', label: 'Point of Sale', icon: CreditCard, path: '/business/pos' },
        { id: 'stock', label: 'Stock Management', icon: Package, path: '/business/stock' },
        { id: 'vouchers', label: 'Vouchers', icon: Tag, path: '/business/vouchers' }
      ]
    },
    { id: 'bookings', label: 'Bookings', icon: CalendarDays, path: '/business/bookings' },
    { id: 'employees', label: 'Employees', icon: Users, path: '/employees' },
    // [DISABLED] Leave — hidden per business decision, re-enable when ready to launch as new features
    // { id: 'leave-management', label: 'Leave Management', icon: CalendarCheck, path: '/business/leave' },
    { id: 'time-tracking', label: 'Time Tracking', icon: Clock, path: '/business/time-tracking' },
    {
      id: 'checklists-goals',
      label: 'Checklists & Goals',
      icon: CheckSquare,
      hasDropdown: true,
      children: [
        // [DISABLED] Checklists — hidden per business decision, re-enable when ready to launch as new features
        // { id: 'checklists', label: 'Checklists', icon: CheckSquare, path: '/checklists' },
        { id: 'goal-setting', label: 'Goal Setting', icon: Target, path: '/business/goal-setting' }
      ]
    },
    { id: 'business-profile', label: 'Business Profile', icon: Building, path: '/business-profile' }
    // [DISABLED] HR Policies & MVV — hidden per business decision, re-enable when ready to launch as new features
    // { id: 'policies', label: 'HR Policies', icon: FileText, path: '/policies' },
    // { id: 'company-values', label: 'Mission & Values', icon: Heart, path: '/company-values' }
  ], [isOwner, isManager]);

  // Flatten nested menu items for search
  const flattenMenuItems = (items) => {
    return items.reduce((acc, item) => {
      if (item.hasDropdown && item.children) {
        return [...acc, ...item.children];
      }
      if (item.path) {
        return [...acc, item];
      }
      return acc;
    }, []);
  };

  // Filter menu items based on search query
  const filteredResults = useMemo(() => {
    const allItems = flattenMenuItems(menuItems);

    if (!searchQuery.trim()) {
      return recentPages.map(recentItem => {
        const menuItem = allItems.find(item => item.id === recentItem.id);
        return menuItem || recentItem;
      }).slice(0, 5);
    }

    const query = searchQuery.toLowerCase();
    return allItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.id.toLowerCase().includes(query) ||
      (item.path && item.path.toLowerCase().includes(query))
    ).slice(0, 8);
  }, [searchQuery, menuItems, recentPages]);

  // Handle navigation from search palette
  const handleSearchNavigate = useCallback((item) => {
    if (item.path) {
      navigate(item.path);

      const recentItem = { id: item.id, label: item.label, path: item.path };
      const newRecent = [recentItem, ...recentPages.filter(p => p.id !== item.id)].slice(0, 5);
      setRecentPages(newRecent);
      localStorage.setItem('recentPagesBusinessLayout', JSON.stringify(newRecent));

      setIsSearchOpen(false);
      setSearchQuery('');
      setSelectedIndex(0);
      setIsMobileMenuOpen(false);
    }
  }, [navigate, recentPages]);

  // Keyboard shortcuts for search palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        setSearchQuery('');
        setSelectedIndex(0);
      }

      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSelectedIndex(0);
      }

      if (isSearchOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => {
            const maxIndex = filteredResults.length - 1;
            return prev < maxIndex ? prev + 1 : prev;
          });
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (filteredResults[selectedIndex]) {
            handleSearchNavigate(filteredResults[selectedIndex]);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, selectedIndex, filteredResults, handleSearchNavigate]);

  // Auto-expand the section containing the active route
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.hasDropdown && item.children) {
        const hasActiveChild = item.children.some(child => location.pathname === child.path);
        if (hasActiveChild) {
          setExpandedSections(prev => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [location.pathname, menuItems]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Render sidebar nav content (shared between desktop and mobile)
  const renderSidebarNav = () => (
    <>
      {menuItems.map((item) => {
        if (item.hasDropdown && item.children) {
          const Icon = item.icon;
          const isLockedSection = !hasFullAccess && item.id !== 'business-profile';
          const isExpanded = expandedSections[item.id];
          const hasActiveChild = item.children.some(child => location.pathname === child.path);
          return (
            <div key={item.id} className={styles.sidebarSection}>
              <button
                className={`${styles.sidebarSectionToggle} ${hasActiveChild ? styles.sidebarSectionToggleActive : ''} ${isLockedSection ? styles.sidebarItemLocked : ''}`}
                onClick={() => isLockedSection ? null : toggleSection(item.id)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
                {isLockedSection
                  ? <Lock size={12} style={{ marginLeft: 'auto', color: '#94a3b8' }} />
                  : <ChevronDown size={14} className={`${styles.sectionChevron} ${isExpanded ? styles.sectionChevronOpen : ''}`} />
                }
              </button>
              {!isLockedSection && isExpanded && (
                <div className={styles.sidebarChildren}>
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const childIsActive = location.pathname === child.path;
                    const isChildLocked = !hasFullAccess && child.id !== 'billing';
                    return (
                      <button
                        key={child.id}
                        className={`${styles.sidebarChildItem} ${childIsActive ? styles.sidebarItemActive : ''} ${isChildLocked ? styles.sidebarItemLocked : ''}`}
                        onClick={() => isChildLocked ? null : handleNavigation(child)}
                      >
                        <ChildIcon size={14} />
                        <span>{child.label}</span>
                        {isChildLocked && <Lock size={12} style={{ marginLeft: 'auto', color: '#94a3b8' }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const isLockedItem = !hasFullAccess && item.id !== 'business-profile';
        return (
          <button
            key={item.id}
            className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ''} ${isLockedItem ? styles.sidebarItemLocked : ''}`}
            onClick={() => isLockedItem ? null : handleNavigation(item)}
          >
            <Icon size={18} />
            <span>{item.label}</span>
            {isLockedItem && <Lock size={12} style={{ marginLeft: 'auto', color: '#94a3b8' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className={`${styles.appLayout} ${isDarkMode ? styles.darkMode : ''}`}>
      {/* Topbar */}
      <header className={`${styles.topbar} ${isDarkMode ? styles.darkHeader : ''}`}>
        <div className={styles.topbarLeft}>
          {/* Mobile hamburger */}
          <button
            className={styles.mobileMenuBtn}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Logo */}
          <div
            className={styles.logoContainer}
            onClick={() => navigate('/dashboard')}
          >
            <img src="/LogoXtraBold.svg" alt="1nespa" className={styles.logoImage} />
            <div className={styles.logoTextContainer}>
              <div className={styles.logoBrandName}>1ne s.p.a.</div>
              <div className={styles.logoSubtitle}>Beauty & Business Coach</div>
            </div>
          </div>
        </div>

        <div className={styles.topbarRight}>
          {/* Search Trigger */}
          <button
            className={styles.searchTrigger}
            onClick={() => setIsSearchOpen(true)}
          >
            <Search size={16} className={styles.searchIcon} />
            <span className={styles.searchPlaceholder}>Search...</span>
          </button>

          {/* Notifications */}
          <div className={styles.notificationSection} ref={notificationDropdownRef}>
            <button
              className={styles.notificationBtn}
              onClick={() => {
                const opening = !isNotificationDropdownOpen;
                setIsNotificationDropdownOpen(opening);
                if (opening) {
                  const unreadNotifications = notifications.filter(n => !n.read);
                  if (unreadNotifications.length > 0) {
                    unreadNotifications.forEach(n => locallyReadIdsRef.current.add(n.id));
                    setNotifications(prev =>
                      prev.map(n => n.read ? n : { ...n, read: true })
                    );
                    unreadNotifications.forEach(n => {
                      apiServiceWithRefresh.markNotificationAsRead(n.id).catch(() => {});
                    });
                  }
                }
              }}
            >
              <Bell size={24} />
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>{unreadCount}</span>
              )}
            </button>

            {isNotificationDropdownOpen && (
              <div className={styles.notificationDropdown}>
                <div className={styles.notificationHeader}>
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <span className={styles.notificationCount}>{unreadCount} new</span>
                  )}
                </div>

                <div className={styles.notificationsList}>
                  {notifications.filter(n => !n.dismissed).length === 0 ? (
                    <div className={styles.emptyNotifications}>
                      <Bell size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyText}>No new notifications</p>
                      <p className={styles.emptySubtext}>You're all caught up!</p>
                    </div>
                  ) : (
                    notifications.filter(n => !n.dismissed).map(notification => (
                      <div
                        key={notification.id}
                        className={`${styles.notificationItem} ${notification.read ? styles.read : styles.unread}`}
                      >
                        <div className={styles.notificationContent}>
                          <h4 className={styles.notificationTitle}>{notification.title}</h4>
                          <p className={styles.notificationMessage}>{notification.message}</p>
                          <span className={styles.notificationTime}>{notification.time}</span>
                        </div>
                        <div className={styles.notificationActions}>
                          <button
                            className={styles.dismissBtn}
                            onClick={() => handleDismissFromDropdown(notification.id)}
                            title="Dismiss"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className={styles.notificationFooter}>
                  <button className={styles.showAllBtn} onClick={handleShowAllMessages}>
                    Show all messages
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className={styles.profileSection} ref={profileDropdownRef}>
            <button
              className={`${styles.profileBtn} ${isDarkMode ? styles.darkProfileBtn : ''}`}
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            >
              <div className={styles.profileAvatar}>
                <User size={16} />
              </div>
              <div className={styles.profileInfo}>
                <span className={styles.profileName}>{displayName}</span>
                <span className={styles.profileRole}>{roleLabel}</span>
              </div>
            </button>

            {isProfileDropdownOpen && (
              <div className={`${styles.profileDropdown} ${isDarkMode ? styles.darkDropdown : ''}`}>
                {(isOwner || isManager) && hasFullAccess && (
                  <div className={styles.profileDropdownItem} onClick={handleViewPersonalProfile}>
                    <User size={16} />
                    <span>User Profile</span>
                  </div>
                )}
                {hasFullAccess && (
                  <div className={styles.profileDropdownItem} onClick={handleViewBusinessProfile}>
                    <Building size={16} />
                    <span>Business Profile</span>
                  </div>
                )}
                {hasFullAccess && (
                  <div className={styles.profileDropdownItem} onClick={handleViewBusinessSettings}>
                    <Settings size={16} />
                    <span>Business Settings</span>
                  </div>
                )}
                <div className={styles.profileDropdownItem} onClick={handleViewBilling}>
                  <Receipt size={16} />
                  <span>Billing</span>
                </div>
                <div className={styles.dropdownDivider}></div>
                <div className={styles.profileDropdownItem} onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Layout Body */}
      <div className={styles.layoutBody}>
        {/* Desktop Sidebar */}
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav}>
            {renderSidebarNav()}
          </nav>
          <div className={styles.sidebarLogout}>
            <button className={styles.sidebarLogoutBtn} onClick={handleLogout}>
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Area */}
        <div className={styles.mainArea}>
          {/* Subscription Inactive Banner */}
          {!hasFullAccess && (
            <div style={{
              backgroundColor: '#fef3c7',
              borderBottom: '1px solid #fcd34d',
              padding: '10px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <AlertTriangle size={16} />
              <span>Your subscription is inactive.</span>
              <button
                onClick={() => navigate('/business/billing')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d97706',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  padding: 0
                }}
              >
                Reactivate now
              </button>
            </div>
          )}

          {/* Grace Period Warning Banner */}
          {isInGracePeriod && (() => {
            const nextBilling = user?.nextBillingDate ? new Date(user.nextBillingDate) : null;
            const graceDays = user?.gracePeriodDays ?? 30;
            let daysRemaining = graceDays;
            if (nextBilling) {
              const deadline = new Date(nextBilling.getTime() + graceDays * 24 * 60 * 60 * 1000);
              daysRemaining = Math.max(0, Math.ceil((deadline - new Date()) / (24 * 60 * 60 * 1000)));
            }
            return (
              <div style={{
                backgroundColor: '#fffbeb',
                borderBottom: '1px solid #fcd34d',
                padding: '10px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#92400e'
              }}>
                <AlertTriangle size={16} />
                <span>
                  Your subscription payment is overdue. You have <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong> remaining before your account is deactivated.
                </span>
                <button
                  onClick={() => navigate('/business/billing')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#d97706',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    padding: 0
                  }}
                >
                  Pay now
                </button>
              </div>
            );
          })()}

          {/* Main Content */}
          <main className={`${styles.mainContent} ${isDarkMode ? styles.darkMainContent : ''}`}>
            <div className={styles.contentContainer}>
              {children || <Outlet />}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.mobileOverlayOpen : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Sidebar Drawer */}
      <aside className={`${styles.mobileSidebar} ${isMobileMenuOpen ? styles.mobileSidebarOpen : ''}`}>
        <nav className={styles.sidebarNav}>
          {renderSidebarNav()}
        </nav>
        <div className={styles.sidebarLogout}>
          <button className={styles.sidebarLogoutBtn} onClick={handleLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Notification Management Modal */}
      {isNotificationModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsNotificationModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>All Notifications</h2>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setIsNotificationModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className={styles.notificationFilters}>
              <div className={styles.notificationSearchWrapper}>
                <Search size={18} className={styles.notificationSearchIcon} />
                <input
                  type="text"
                  className={styles.notificationSearchInput}
                  placeholder="Search notifications..."
                  value={notificationSearch}
                  onChange={(e) => setNotificationSearch(e.target.value)}
                />
              </div>
              <select
                className={styles.notificationFilterSelect}
                value={notificationTypeFilter}
                onChange={(e) => setNotificationTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="checklist_assigned">Assigned</option>
                <option value="checklist_submitted">Submitted</option>
                <option value="checklist_reviewed">Reviewed</option>
              </select>
              <select
                className={styles.notificationFilterSelect}
                value={notificationReadFilter}
                onChange={(e) => setNotificationReadFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>

            <div className={styles.modalBody}>
              {notifications.length === 0 ? (
                <div className={styles.modalEmptyState}>
                  <Bell size={64} className={styles.emptyIcon} />
                  <p className={styles.emptyText}>No notifications</p>
                  <p className={styles.emptySubtext}>You're all caught up!</p>
                </div>
              ) : filteredModalNotifications.length === 0 ? (
                <div className={styles.modalEmptyState}>
                  <Search size={48} className={styles.emptyIcon} />
                  <p className={styles.emptyText}>No matching notifications</p>
                  <p className={styles.emptySubtext}>Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className={styles.modalNotificationsList}>
                  {filteredModalNotifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`${styles.modalNotificationItem} ${notification.read ? styles.read : styles.unread}`}
                    >
                      <div className={styles.notificationContent}>
                        <h4 className={styles.notificationTitle}>{notification.title}</h4>
                        <p className={styles.notificationMessage}>{notification.message}</p>
                        <span className={styles.notificationTime}>{notification.time}</span>
                      </div>
                      <div className={styles.notificationActions}>
                        <button
                          className={styles.dismissBtn}
                          onClick={() => handleDismissNotification(notification.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.btnSecondary}
                onClick={() => setIsNotificationModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Widget */}
      <SupportWidget />

      {/* Command Palette Modal */}
      {isSearchOpen && (
        <div
          className={styles.commandPaletteOverlay}
          onClick={() => setIsSearchOpen(false)}
        >
          <div
            className={styles.commandPalette}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.commandPaletteHeader}>
              <Search size={20} className={styles.commandSearchIcon} />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                className={styles.commandSearchInput}
                autoFocus
              />
              <kbd className={styles.escHint}>Esc</kbd>
            </div>

            <div className={styles.commandPaletteResults}>
              {filteredResults.length === 0 ? (
                <div className={styles.noResults}>
                  <p>No pages found</p>
                </div>
              ) : (
                <>
                  {searchQuery === '' && recentPages.length > 0 && (
                    <div className={styles.resultSection}>
                      <div className={styles.resultSectionLabel}>Recent</div>
                    </div>
                  )}
                  {filteredResults.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        className={`${styles.commandPaletteItem} ${index === selectedIndex ? styles.commandPaletteItemSelected : ''}`}
                        onClick={() => handleSearchNavigate(item)}
                      >
                        {Icon && typeof Icon === 'function' && <Icon size={18} className={styles.resultIcon} />}
                        <span className={styles.resultLabel}>{item.label}</span>
                        <span className={styles.resultPath}>{item.path}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className={styles.commandPaletteFooter}>
              <div className={styles.commandHints}>
                <span><kbd>↑↓</kbd> Navigate</span>
                <span><kbd>Enter</kbd> Select</span>
                <span><kbd>Esc</kbd> Close</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessLayout;
